import { Trans } from "react-i18next";
import { createElement, useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { AnnualWheelActivityStandardTemplateModel, AnnualWheelActivityStandardTemplateTableModel } from "./AnnualWheelActivityStandardTemplate.types";
import { deleteHttp } from "../../common/api/apiShared";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { useTranslation } from "../../localization/useTranslation.ts";
import { useRecurringIntervalFormatting } from "../../annualWheel/AnnualWheel.hooks.ts";
import { useUrlProvider } from "../../useUrlProvider.ts";
import { useStateUrlParamsArray } from "../../common/hooks/useStateUrlParams.ts";
import { DotLegalSelectOption } from "../../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types.ts";
import { isNullOrWhitespace } from "../../common/stringOperations.ts";
import { useDotLegalQuery, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useAnnualWheelActivityStandardTemplateList() {
    const url = "/AnnualWheelActivityStandardTemplates";
    const { isLoading, data: templates, refetch } = useDotLegalQuery<Array<AnnualWheelActivityStandardTemplateModel>>(url);

    const { permissions, language } = useUserContext();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [idForUpdate, setIdForUpdate] = useState<string>();
    const { translateString, translateAnnualWheelActivityGeneralTypeEnum } = useTranslation();
    const [templateForDeletion, setTemplateForDeletion] = useState<AnnualWheelActivityStandardTemplateTableModel | undefined>(undefined);
    const [searchedAreas, setSearchedAreas] = useStateUrlParamsArray("areas", []);
    const [searchedAnnualWheelActivityGeneralTypes, setSearchedAnnualWheelActivityGeneralTypes] = useStateUrlParamsArray("generalTypes", []);
    const [searchedStatus, setSearchedStatus] = useStateUrlParamsArray("statuses", []);
    const snackBar = useDotLegalSnackbar();
    const formatRecurringInterval = useRecurringIntervalFormatting();
    let urlProvider = useUrlProvider();

    let areasFilterOptions: Array<DotLegalSelectOption> = [];
    let annualWheelActivityGeneralTypesFilterOptions: Array<DotLegalSelectOption> = [];
    if (templates) {
        areasFilterOptions = Array.from(
            new Map(templates.flatMap((template) => template.complianceAreas).map((pair) => [pair.id, { id: pair.id, name: pair.name }])).values()
        );
        const generalTypes = templates.filter((template) => template?.generalType).map((template) => template.generalType!);

        const uniqueGeneralTypes = Array.from(new Set(generalTypes));
        annualWheelActivityGeneralTypesFilterOptions = uniqueGeneralTypes.map((generalType) => {
            return { id: generalType.toString(), name: translateAnnualWheelActivityGeneralTypeEnum(generalType.toString()) };
        });
    }
    const breadCrumbs = [{ name: "Annual wheel activity standard templates" }];

    const deleteTemplate = async (id: string) => {
        return await deleteHttp(`/AnnualWheelActivityStandardTemplates/${id}`);
    };

    const deleteMutation = useDotLegalMutation(deleteTemplate, {
        onSuccess: async (result) => {
            if (result.success()) {
                await refetch();
                snackBar.show(
                    createElement(Trans, {
                        i18nKey: "itemArchivedSuccess",
                        values: { name: templateForDeletion?.name },
                    })
                );
                setTemplateForDeletion(undefined);
            }
        },
    });

    const onCreateClick = () => {
        setCreateDialogOpen(true);
    };

    const onUpdateClick = (id: string) => {
        setIdForUpdate(id);
    };

    const onSaved = async () => {
        await refetch();
        onDialogClose();
    };

    const onDialogClose = () => {
        setIdForUpdate(undefined);
        setCreateDialogOpen(false);
    };

    const getMenuItems = (template: AnnualWheelActivityStandardTemplateTableModel) => {
        let menuItems = [];
        if (permissions.canManageMasterData) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => {
                    setIdForUpdate(template.id);
                },
            });

            if (!template.archived) {
                menuItems.push({
                    menuItem: translateString("archive"),
                    onClick: () => {
                        setTemplateForDeletion(template);
                    },
                });
            }
        }

        return menuItems;
    };

    const onDelete = () => {
        deleteMutation.mutate(templateForDeletion!.id);
    };

    function getFilteredTemplates(): Array<AnnualWheelActivityStandardTemplateTableModel> {
        let tempData = [...(templates ?? [])];

        if (searchedAreas.length > 0 && !isNullOrWhitespace(searchedAreas[0])) {
            tempData = tempData.filter((row) => row.complianceAreas.filter((x) => searchedAreas.includes(x.id)).length > 0);
        }

        if (searchedAnnualWheelActivityGeneralTypes.length > 0 && !isNullOrWhitespace(searchedAnnualWheelActivityGeneralTypes[0])) {
            tempData = tempData.filter((row) => searchedAnnualWheelActivityGeneralTypes.indexOf(row.generalType?.toString() ?? "") >= 0);
        }

        if (searchedStatus.length > 0 && !isNullOrWhitespace(searchedStatus[0])) {
            tempData = tempData.filter((row) => searchedStatus.indexOf(row.archived ? "0" : "1") >= 0);
        }

        return tempData.map((t) => {
            let lang = t.languages.find((l) => l.twoLetterIsoLanguage == language)!;
            return {
                ...t,
                name: lang.name,
                description: lang.description,
                complianceAreaString: t.complianceAreas.map((c) => c.name).join(", "),
                recurringIntervalString: formatRecurringInterval(t.recurringInterval, t.recurringIntervalType),
                statusString: t.archived ? translateString("archived") : translateString("active"),
            };
        });
    }

    return {
        isLoading,
        getFilteredTemplates,
        breadCrumbs,
        onSaved,
        onCreateClick,
        createDialogOpen,
        onUpdateClick,
        onDialogClose,
        templateToUpdate: templates?.find((t) => t.id === idForUpdate),
        getMenuItems,
        templateForDeletion,
        setTemplateForDeletion,
        onDelete,
        annualWheelActivityGeneralTypesFilterOptions,
        searchedAnnualWheelActivityGeneralTypes,
        setSearchedAnnualWheelActivityGeneralTypes,
        searchedAreas,
        setSearchedAreas,
        areasFilterOptions,
        searchedStatus,
        setSearchedStatus,
    };
}
